import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Tags, Building2, Loader2, Search, ChevronRight, AlertTriangle } from "lucide-react";

export default function AdminCategories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryTree, setCategoryTree] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Category selection for account assignment
  const [selectedPrimary, setSelectedPrimary] = useState("");
  const [selectedSecondary, setSelectedSecondary] = useState("");
  const [selectedTertiary, setSelectedTertiary] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, accountsRes] = await Promise.all([
        axios.get(`${API}/categories/tree`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCategoryTree(categoriesRes.data);
      setAccounts(accountsRes.data);
      
      // Flatten categories for quick lookup
      const flat = [];
      categoriesRes.data.forEach(primary => {
        flat.push(primary);
        (primary.children || []).forEach(secondary => {
          flat.push(secondary);
          (secondary.children || []).forEach(tertiary => {
            flat.push(tertiary);
          });
        });
      });
      setCategories(flat);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || "Unknown";
  };

  const openAssignDialog = (account) => {
    setSelectedAccount(account);
    setSelectedPrimary(account.category_ids?.primary_id || "");
    setSelectedSecondary(account.category_ids?.secondary_id || "");
    setSelectedTertiary(account.category_ids?.tertiary_id || "");
    setDialogOpen(true);
  };

  const handleSaveCategories = async () => {
    if (!selectedAccount) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/accounts/${selectedAccount.id}/categories`, {
        primary_id: selectedPrimary || null,
        secondary_id: selectedSecondary || null,
        tertiary_id: selectedTertiary || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Categories updated successfully");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update categories");
    } finally {
      setSaving(false);
    }
  };

  const getSecondaryOptions = () => {
    if (!selectedPrimary) return [];
    const primary = categoryTree.find(c => c.id === selectedPrimary);
    return primary?.children || [];
  };

  const getTertiaryOptions = () => {
    if (!selectedSecondary) return [];
    const primary = categoryTree.find(c => c.id === selectedPrimary);
    const secondary = primary?.children?.find(c => c.id === selectedSecondary);
    return secondary?.children || [];
  };

  const filteredAccounts = accounts.filter(acc => 
    acc.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.abn?.includes(searchTerm)
  );

  const uncategorizedCount = accounts.filter(a => !a.category_ids?.primary_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Business Categories</h1>
          <p className="text-muted-foreground">Assign categories to advertisers for competitor targeting</p>
        </div>
      </div>

      {/* Alert for uncategorized accounts */}
      {uncategorizedCount > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="font-medium text-yellow-500">{uncategorizedCount} advertisers without categories</p>
              <p className="text-sm text-muted-foreground">
                Assign categories to enable competitor ad redirection for these advertisers.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Advertisers</p>
                <p className="text-2xl font-bold mt-1">{accounts.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Categorized</p>
                <p className="text-2xl font-bold mt-1 text-green-500">{accounts.filter(a => a.category_ids?.primary_id).length}</p>
              </div>
              <Tags className="h-8 w-8 text-green-500/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Uncategorized</p>
                <p className="text-2xl font-bold mt-1 text-yellow-500">{uncategorizedCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Categories</p>
                <p className="text-2xl font-bold mt-1">{categories.length}</p>
              </div>
              <Tags className="h-8 w-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Tree Preview */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Category Hierarchy
          </CardTitle>
          <CardDescription>Available business categories for competitor matching</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categoryTree.map(primary => (
              <div key={primary.id} className="space-y-1">
                <p className="font-semibold text-primary">{primary.name}</p>
                <div className="text-xs text-muted-foreground space-y-0.5 pl-2">
                  {(primary.children || []).slice(0, 4).map(sec => (
                    <p key={sec.id} className="flex items-center gap-1">
                      <ChevronRight className="h-3 w-3" />
                      {sec.name}
                    </p>
                  ))}
                  {(primary.children?.length || 0) > 4 && (
                    <p className="text-primary/60">+{primary.children.length - 4} more</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search advertisers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-advertisers"
        />
      </div>

      {/* Advertisers Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Primary Category</TableHead>
                  <TableHead>Secondary</TableHead>
                  <TableHead>Tertiary</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map(account => (
                  <TableRow 
                    key={account.id} 
                    data-testid={`category-row-${account.id}`}
                    className={!account.category_ids?.primary_id ? "bg-yellow-500/5" : ""}
                  >
                    <TableCell>
                      <div>
                        <p className="font-semibold">{account.business_name}</p>
                        <p className="text-xs text-muted-foreground">{account.tier}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {account.category_ids?.primary_id ? (
                        <Badge variant="outline" className="bg-primary/10">
                          {getCategoryName(account.category_ids.primary_id)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {account.category_ids?.secondary_id ? (
                        <Badge variant="outline">
                          {getCategoryName(account.category_ids.secondary_id)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {account.category_ids?.tertiary_id ? (
                        <Badge variant="outline">
                          {getCategoryName(account.category_ids.tertiary_id)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={account.status === "ACTIVE" ? "status-active" : "status-paused"}>
                        {account.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant={account.category_ids?.primary_id ? "outline" : "default"}
                        onClick={() => openAssignDialog(account)}
                        data-testid={`assign-categories-${account.id}`}
                      >
                        {account.category_ids?.primary_id ? "Edit" : "Assign"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Assign Categories Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Categories - {selectedAccount?.business_name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Assign business categories to enable competitor ad targeting. When users travel to 
              competing businesses in the same category, ads from this advertiser may be shown.
            </p>
            
            {/* Primary Category */}
            <div className="space-y-2">
              <Label>Primary Category <span className="text-red-500">*</span></Label>
              <Select
                value={selectedPrimary}
                onValueChange={(val) => {
                  setSelectedPrimary(val);
                  setSelectedSecondary("");
                  setSelectedTertiary("");
                }}
              >
                <SelectTrigger data-testid="select-primary">
                  <SelectValue placeholder="Select primary category" />
                </SelectTrigger>
                <SelectContent>
                  {categoryTree.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Secondary Category */}
            <div className="space-y-2">
              <Label>Secondary Category</Label>
              <Select
                value={selectedSecondary}
                onValueChange={(val) => {
                  setSelectedSecondary(val);
                  setSelectedTertiary("");
                }}
                disabled={!selectedPrimary}
              >
                <SelectTrigger data-testid="select-secondary">
                  <SelectValue placeholder={selectedPrimary ? "Select secondary" : "Select primary first"} />
                </SelectTrigger>
                <SelectContent>
                  {getSecondaryOptions().map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tertiary Category */}
            <div className="space-y-2">
              <Label>Tertiary Category</Label>
              <Select
                value={selectedTertiary}
                onValueChange={setSelectedTertiary}
                disabled={!selectedSecondary}
              >
                <SelectTrigger data-testid="select-tertiary">
                  <SelectValue placeholder={selectedSecondary ? "Select tertiary" : "Select secondary first"} />
                </SelectTrigger>
                <SelectContent>
                  {getTertiaryOptions().map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-secondary/30 rounded-lg p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">How competitor targeting works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Users traveling to non-advertising businesses will see ads from competitors</li>
                <li>Matching is based on category - more specific = better match</li>
                <li>Ads only appear within 500m of the competitor destination</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveCategories}
              disabled={saving || !selectedPrimary}
              data-testid="save-categories-btn"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Categories
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
